import { Icon } from './Icons';
import React, { useState, useEffect } from 'react';
import { loadAllBloodMarkers, loadLatestMetrics } from '../db';
import { BIOMARKER_GROUPS, getStatus } from '../biomarkers';
import { auth, db } from '../firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

async function saveNotificationPrefs(prefs) {
  const user = auth.currentUser;
  if (!user) return;
  await setDoc(doc(db, 'users', user.uid, 'latest', 'notificationPrefs'), { ...prefs, updatedAt: serverTimestamp() });
}

async function loadNotificationPrefs() {
  const user = auth.currentUser;
  if (!user) return null;
  const snap = await getDoc(doc(db, 'users', user.uid, 'latest', 'notificationPrefs'));
  return snap.exists() ? snap.data() : null;
}

function Toggle({ label, desc, value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.85rem 1rem', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', marginBottom: '.5rem' }}>
      <div style={{ flex: 1, marginRight: '1rem' }}>
        <div style={{ fontSize: '.82rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.15rem' }}>{label}</div>
        <div style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>{desc}</div>
      </div>
      <button onClick={() => onChange(!value)} style={{
        width: '44px', height: '24px', borderRadius: '12px', border: 'none',
        cursor: 'pointer', position: 'relative', flexShrink: 0,
        background: value ? 'var(--primary)' : 'rgba(255,255,255,0.1)',
        transition: 'background .2s'
      }}>
        <div style={{ position: 'absolute', top: '2px', left: value ? '22px' : '2px', width: '20px', height: '20px', borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
      </button>
    </div>
  );
}

export default function Notifications() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [flags, setFlags] = useState([]);
  const [prefs, setPrefs] = useState({
    emailOnFlag: true,
    emailOnUpload: false,
    weeklyDigest: true,
    notificationEmail: auth.currentUser?.email || ''
  });

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [all, p] = await Promise.all([loadAllBloodMarkers(), loadNotificationPrefs()]);
        if (p) setPrefs(prev => ({ ...prev, ...p }));
        const flagged = [];
        if (all) {
          Object.entries(BIOMARKER_GROUPS).forEach(([gk, group]) => {
            Object.entries(group.markers).forEach(([mk, marker]) => {
              if (all[mk]) {
                const status = getStatus(gk, mk, all[mk]);
                if (status === 'warn' || status === 'bad') {
                  flagged.push({ marker: marker.label, value: all[mk], unit: marker.unit, status, group: group.label });
                }
              }
            });
          });
        }
        setFlags(flagged);
      } catch (e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      await saveNotificationPrefs(prefs);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { alert('Error saving preferences.'); }
    setSaving(false);
  }

  if (loading) return (
    <div style={{ padding: '2rem 0' }}>
      <div style={{ width: '160px', height: '28px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', marginBottom: '.5rem' }} />
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.6rem', fontWeight: 600, color: 'var(--text)', letterSpacing: '-.02em', marginBottom: '.2rem' }}>Notifications</h1>
        <p style={{ fontSize: '.77rem', color: 'var(--text3)', fontWeight: 300 }}>Manage how Levare keeps you informed about your health</p>
      </div>

      {/* Flagged markers */}
      {flags.length > 0 ? (
        <div style={{ background: 'var(--bg-card)', border: '1px solid rgba(240,160,80,.25)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '1rem' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--warning)', animation: 'pulse 2s infinite' }} />
            <div style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--warning)', textTransform: 'uppercase', letterSpacing: '.1em' }}>
              {flags.length} marker{flags.length !== 1 ? 's' : ''} outside optimal range
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem', marginBottom: '1rem' }}>
            {flags.map((f, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '.65rem .9rem',
                background: f.status === 'bad' ? 'rgba(240,80,80,.06)' : 'rgba(240,160,80,.06)',
                borderRadius: 'var(--radius)',
                border: `1px solid ${f.status === 'bad' ? 'rgba(240,80,80,.2)' : 'rgba(240,160,80,.2)'}`
              }}>
                <div>
                  <span style={{ fontSize: '.82rem', color: 'var(--text)', fontWeight: 400 }}>{f.marker}</span>
                  <span style={{ fontSize: '.68rem', color: 'var(--text3)', marginLeft: '.5rem', fontWeight: 300 }}>{f.group}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                  <span style={{ fontSize: '.82rem', fontWeight: 600, color: f.status === 'bad' ? 'var(--danger)' : 'var(--warning)' }}>{f.value} {f.unit}</span>
                  <span style={{ fontSize: '.62rem', padding: '.15rem .45rem', borderRadius: '10px', background: f.status === 'bad' ? 'var(--danger-dim)' : 'var(--warning-dim)', color: f.status === 'bad' ? 'var(--danger)' : 'var(--warning)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    {f.status === 'bad' ? 'Outside range' : 'Borderline'}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ padding: '.85rem 1rem', background: 'rgba(0,174,239,0.06)', border: '1px solid rgba(0,174,239,0.15)', borderRadius: 'var(--radius)', fontSize: '.75rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6 }}>
            <Icon name="message" size={14} /> Speak with Rico in the AI Advisor for personalised guidance on these markers, or contact your GP if you have concerns.
          </div>
        </div>
      ) : (
        <div style={{ background: 'var(--bg-card)', border: '1px solid rgba(0,200,150,.2)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '.75rem' }}>
          <div style={{ fontSize: '1.2rem' }}><Icon name="check-circle" size={14} /></div>
          <div style={{ fontSize: '.82rem', color: 'var(--success)', fontWeight: 400 }}>All your recorded markers are currently within optimal ranges.</div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>

        {/* Preferences */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Notification Preferences</div>

          <Toggle label="Alert when markers are flagged" desc="Notified when a result falls outside optimal range" value={prefs.emailOnFlag} onChange={v => setPrefs(p => ({ ...p, emailOnFlag: v }))} />
          <Toggle label="Confirm when data is saved" desc="Confirmation each time you upload results" value={prefs.emailOnUpload} onChange={v => setPrefs(p => ({ ...p, emailOnUpload: v }))} />
          <Toggle label="Weekly health digest" desc="A weekly summary of your metrics and Rico's insights" value={prefs.weeklyDigest} onChange={v => setPrefs(p => ({ ...p, weeklyDigest: v }))} />

          <div style={{ marginTop: '1rem' }}>
            <label style={{ fontSize: '.68rem', color: 'var(--text3)', display: 'block', marginBottom: '.4rem', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}>Notification Email</label>
            <input type="email" value={prefs.notificationEmail} onChange={e => setPrefs(p => ({ ...p, notificationEmail: e.target.value }))} placeholder="your@email.com" style={{ width: '100%', marginBottom: '.75rem' }} />
          </div>

          {saved && (
            <div style={{ padding: '.6rem .85rem', borderRadius: 'var(--radius)', fontSize: '.75rem', marginBottom: '.75rem', background: 'var(--success-dim)', border: '1px solid rgba(0,200,150,.2)', color: 'var(--success)' }}>
              ✓ Preferences saved.
            </div>
          )}

          <button onClick={handleSave} disabled={saving} style={{ width: '100%', padding: '.7rem', borderRadius: 'var(--radius)', border: 'none', background: 'var(--primary)', color: '#080f18', fontFamily: 'var(--font-body)', fontSize: '.85rem', cursor: 'pointer', fontWeight: 500, opacity: saving ? .7 : 1 }}>
            {saving ? 'Saving…' : 'Save Preferences'}
          </button>

          <div style={{ marginTop: '.75rem', padding: '.6rem .85rem', background: 'rgba(0,174,239,0.06)', border: '1px solid rgba(0,174,239,0.12)', borderRadius: 'var(--radius)', fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.5 }}>
             Email notifications are coming soon. Your preferences are saved and will activate automatically when email delivery is enabled.
          </div>
        </div>

        {/* Contact Levare */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Contact Levare</div>
          <p style={{ fontSize: '.78rem', color: 'var(--text3)', marginBottom: '1.25rem', fontWeight: 300, lineHeight: 1.6 }}>
            Have a question about your results? Want to speak with a Levare clinician? Get in touch directly.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
            {[
              { href: 'mailto:contact@levarewellbeing.com', icon: '', title: 'Email the team', sub: 'contact@levarewellbeing.com', bg: 'var(--bg-elevated)', color: 'var(--text)', border: '1px solid var(--border2)' },
              { href: 'https://cal.com/levare-wellbeing/30min', icon: 'calendar', title: 'Book a 30-min call', sub: 'Schedule directly with Levare', bg: 'var(--primary)', color: '#080f18', border: 'none' },
              { href: 'https://levarewellbeing.com', icon: '', title: 'Visit levarewellbeing.com', sub: 'Explore all our programmes', bg: 'var(--bg-elevated)', color: 'var(--text)', border: '1px solid var(--border2)' },
            ].map(link => (
              <a key={link.title} href={link.href} target={link.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: '.75rem', padding: '.85rem 1rem', background: link.bg, borderRadius: 'var(--radius)', textDecoration: 'none', border: link.border, transition: 'opacity .15s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <span style={{ fontSize: '1.1rem', flexShrink: 0 }}><Icon name={link.icon} size={16} /></span>
                <div>
                  <div style={{ fontSize: '.8rem', fontWeight: 500, color: link.color }}>{link.title}</div>
                  <div style={{ fontSize: '.7rem', color: link.color, opacity: 0.7, marginTop: '.1rem' }}>{link.sub}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
    </div>
  );
}
